import type {Weekday} from '@gl/lit/common/constants/dates.js';
import type {Tag} from './audiences.js';
import type {ISOString, Nullable} from './general.js';
import type {
  AudienceFrequencyEnum,
  AudienceScheduleEndTypeEnum,
  AudienceScheduleStartTypeEnum,
} from './journey/audience_frequency.js';
import type {User} from './user.js';
import type {Option} from '@gl/lit/components/base/multiselect_async/index.js';
import type {AudienceNodeSchedule} from '@gl/lit/components/journey_builder/destination_node/utils.js';
import type {AdditionalFieldResponseValue} from './audience_exports.js';
import type {EmailTemplate, SenderEmail} from './journey/paid_media_export.js';
import type {DataExtensionsResponse} from '@gl/lit/components/audiences/audience_export_form/utils.js';
import type {DatasetGroup} from './datasets.js';
import type {JourneyType} from './journey/schedule.js';
import type {MappedMatchField, MatchType} from '@gl/types/journeys.js';
import type {Audience} from '@gl/lit/types/audiences.js';

export interface Journey {
  id: number;
  name: string;
  dataset_group_id: number;
  audience_id: number;
  status: string;
  settings: object;
  graph: object;
  tag_list: Tag[];
  creator_metadata: User;
  audience_metadata: Record<string, string>;
  dataset_group_metadata: Record<string, string>;
  statistics: Record<string, string>;
  updated_at: Date;
  created_at: string;
}

export type AudienceConfirmModal = {
  open: boolean;
  previousAudience: Nullable<Audience>;
  newAudience: Nullable<Audience>;
};

export interface EntryNodeConfig {
  journeyType: JourneyType;
  datasetGroups: DatasetGroup[];
  datasetGroup: DatasetGroup;
  realtimeEvent: QueueEvent;
  realtimeEvents: QueueEvent[];
}

export enum ManualModeDirection {
  //run until this node
  UPSTREAM = 'upstream',
  //run only this node
  SELF = 'self',
  //run from this node
  DOWNSTREAM = 'downstream',
}

//FIXME (Jeff) look to extend/alias the QueueEvent type
export interface RealtimeEvent {
  id: number;
  dataset_group_id: number;
  queue_event_id: number;
  dataset_id: number;
  field_mapping: {dataset_field: string; queue_event_field: string}[];
  created_at: Nullable<string>;
  updated_at: Nullable<string>;
  deleted_at: Nullable<string>;
  queue_event: QueueEvent;
  dataset: {id: number; name: string};
}

export interface QueueEvent {
  id: number;
  display_name: string;
  queue_event_id: number;
}

export interface RealtimeEventsResponse {
  message: string;
  more: boolean;
  results: RealtimeEvent[];
}

export type matchFieldValue = string;
export type SelectedFieldValue = Nullable<string>;
export type MatchField = {
  label: string;
  value: string;
  compatibleTypes?: string[];
  required?: boolean;
  match_type?: MatchType;
  caption?: string;
};

export enum PlatformType {
  ZETA = 'zeta',
  FACEBOOK = 'fb_ads',
}

export enum WhenToAdvanceUser {
  AFTER_REMOVAL_FROM_AUDIENCE = 'After removal from audience',
  IMMEDIATELY = 'Immediately',
}

export enum ExportConfigurationStep {
  NOT_CONFIGURED = 'not-configured',
  CONFIGURED = 'configured',
  CONFIGURING_EXPORT = 'configuring-export',
  CONFIGURING_PRIVACY_SETTINGS = 'configuring-privacy-settings',
  CONFIGURING_ZETA_FIELDS = 'configuring-zeta-fields',
}

export type ExportConfig = {
  step: ExportConfigurationStep;
  configFormData: Omit<ExportConfigFormData, 'schedule'> & {
    schedule?: AudienceSchedule;
  };
};

export type CanvasNodeExportConfig = {
  step: ExportConfigurationStep;
  configFormData: ExportConfigFormData;
};

export type MlbExportConfig = {
  step: ExportConfigurationStep;
};

export enum RemovalTimeUnits {
  HOURS = 'Hours',
  DAYS = 'Days',
  WEEKS = 'Weeks',
}

export type MarketingCloudExportConfig = {
  marketingCloudBusinessUnitId: string;
  marketingCloudFolder: string;
  existing_list_id: string;
};

export type PulsePointExportConfig = {
  pulsePointAdvertiser: string;
  pulsePointProduct: string;
};

export type QualtricsExportConfig = {
  qualtricsListType: string;
  qualtricsDirectoryId: string;
};

export type DV360ExportConfig = {
  dv360AppId: string;
};

export type GoogleAdsExportConfig = {
  googleAdsAppId: string;
};
export enum CampaignTriggersExportActions {
  // Global
  SEND_EMAIL = 'send_email',

  // SendGrid
  ADD_TO_LIST_ONLY = 'add_to_list_only',

  // SFMC Marketing Cloud
  ADD_TO_NEW_DATA_EXTENSION = 'add_to_new_data_extension',
  ADD_TO_EXISTING_DATA_EXTENSION = 'add_to_existing_data_extension',
}

export type CampaignTriggersExportConfig = {
  allowedSenderEmails: SenderEmail[];
  availableTemplates: EmailTemplate[];
  selectedEmailTemplate: Nullable<EmailTemplate>;

  exportAction: Nullable<CampaignTriggersExportActions>;
  senderEmail: SenderEmail['email'];
  templateID: EmailTemplate['id'];
};

// Extend this to other custom fields we encounter per destination
export type ExportFields =
  | MarketingCloudExportConfig
  | PulsePointExportConfig
  | DV360ExportConfig
  | GoogleAdsExportConfig
  | QualtricsExportConfig
  | CampaignTriggersExportConfig;

export type ExportConfigFormData = {
  platform_type: Nullable<PlatformType>;
  exportName: string;
  duration: number;
  tableName: string;
  userConsent: string;
  whenToAdvanceUser: WhenToAdvanceUser;
  mappedMatchFields: MappedMatchField[];
  personalizationFields: Option[];
  additionalFields: AdditionalFieldResponseValue;
  allPersonalizationFieldsSelected: boolean;
  allGLGeneratedFieldsSelected: boolean;
  isValid: boolean;
  marketo_audience_type: string;
  privacySettings: MLBPrivacySettings;
  fieldMappings: ZetaFieldMappings;
  hasDuplicateAliases?: boolean;
  tableNameValidation: TableNameValidation;
  schedule?: Nullable<AudienceNodeSchedule>;
  exportFields?: ExportFields;
  dataExtensions: DataExtensionsResponse['data_extensions'];
};

export enum DestinationConfigFormField {
  //privacy settings
  LOSP = 'losp',
  END_USER_OPT_IN = 'end_user_opt_in',
  THIRD_PARTY_NAME = 'third_party_name',
  CLUB_SPONSOR_ADVERTISER_IO = 'club_sponsor_advertiser_io',
  OPT_IN_LANGUAGE_APPROVED_VETTED = 'opt_in_language_approved_vetted',
  CLUB_SPONSOR_ADVERTISER_DATA_SHARE = 'club_sponsor_advertiser_data_share',
  INFO_COLLECTED_VIA_MLB_PRIVACY_POLICY = 'info_collected_via_mlb_privacy_policy',

  //field mappings
  VARIATION = 'variation',
  EMAIL_TYPE = 'email_type',
  CAMPAIGN_ID = 'campaign_id',
  EMAIL_LIST_ID = 'email_list_id',
  EXPORT_FREQUENCY = 'export_frequency',

  //paid media export fields
  MARKETO_AUDIENCE_TYPE = 'marketo_audience_type',
  PLATFORM_TYPE = 'platform_type',
  EXPORT_NAME = 'exportName',
  DURATION = 'duration',
  WHEN_TO_ADVANCE_USER = 'whenToAdvanceUser',
  MAPPED_MATCH_FIELDS = 'mappedMatchFields',
  PERSONALIZATION_FIELDS = 'personalizationFields',
  ALL_PERSONALIZATION_FIELDS_SELECTED = 'allPersonalizationFieldsSelected',
  ALL_GL_GENERATED_FIELDS_SELECTED = 'allGLGeneratedFieldsSelected',
  TABLE_NAME = 'tableName',
  USER_CONSENT = 'userConsent',

  //Schedule
  AUDIENCE_SCHEDULE = 'schedule',

  //Export Fields
  EXPORT_FIELDS = 'exportFields',

  //Additional Fields
  ADDITIONAL_FIELDS = 'additionalFields',
}

export type DestinationConfigFormFieldTypeMap = {
  [DestinationConfigFormField.LOSP]: string;
  [DestinationConfigFormField.END_USER_OPT_IN]: boolean;
  [DestinationConfigFormField.THIRD_PARTY_NAME]: string;
  [DestinationConfigFormField.CLUB_SPONSOR_ADVERTISER_IO]: string;
  [DestinationConfigFormField.OPT_IN_LANGUAGE_APPROVED_VETTED]: boolean;
  [DestinationConfigFormField.CLUB_SPONSOR_ADVERTISER_DATA_SHARE]: string;
  [DestinationConfigFormField.INFO_COLLECTED_VIA_MLB_PRIVACY_POLICY]: boolean;
  [DestinationConfigFormField.VARIATION]: string;
  [DestinationConfigFormField.EMAIL_TYPE]: string;
  [DestinationConfigFormField.CAMPAIGN_ID]: string;
  [DestinationConfigFormField.EMAIL_LIST_ID]: string;
  [DestinationConfigFormField.EXPORT_FREQUENCY]: string;
  [DestinationConfigFormField.MARKETO_AUDIENCE_TYPE]: string;
  [DestinationConfigFormField.PLATFORM_TYPE]: Nullable<PlatformType>;
  [DestinationConfigFormField.EXPORT_NAME]: string;
  [DestinationConfigFormField.DURATION]: number;
  [DestinationConfigFormField.WHEN_TO_ADVANCE_USER]: WhenToAdvanceUser;
  [DestinationConfigFormField.MAPPED_MATCH_FIELDS]: MappedMatchField[];
  [DestinationConfigFormField.PERSONALIZATION_FIELDS]: Option[];
  [DestinationConfigFormField.ALL_PERSONALIZATION_FIELDS_SELECTED]: boolean;
  [DestinationConfigFormField.ALL_GL_GENERATED_FIELDS_SELECTED]: boolean;
  [DestinationConfigFormField.AUDIENCE_SCHEDULE]: Nullable<AudienceNodeSchedule>;
  [DestinationConfigFormField.EXPORT_FIELDS]?: ExportFields;
  [DestinationConfigFormField.TABLE_NAME]: string;
  [DestinationConfigFormField.USER_CONSENT]: string;
  [DestinationConfigFormField.ADDITIONAL_FIELDS]: AdditionalFieldResponseValue;
};

export const PRIVACY_SETTINGS_FIELD_SET = new Set([
  DestinationConfigFormField.LOSP,
  DestinationConfigFormField.END_USER_OPT_IN,
  DestinationConfigFormField.THIRD_PARTY_NAME,
  DestinationConfigFormField.CLUB_SPONSOR_ADVERTISER_IO,
  DestinationConfigFormField.OPT_IN_LANGUAGE_APPROVED_VETTED,
  DestinationConfigFormField.CLUB_SPONSOR_ADVERTISER_DATA_SHARE,
  DestinationConfigFormField.INFO_COLLECTED_VIA_MLB_PRIVACY_POLICY,
]);

export const ZETA_FIELDS_SET = new Set([
  DestinationConfigFormField.VARIATION,
  DestinationConfigFormField.EMAIL_TYPE,
  DestinationConfigFormField.CAMPAIGN_ID,
  DestinationConfigFormField.EMAIL_LIST_ID,
  DestinationConfigFormField.EXPORT_FREQUENCY,
]);

export type DestinationConfigFormValues = {
  [K in DestinationConfigFormField]: DestinationConfigFormFieldTypeMap[K];
} & {isValid: boolean};

export type FormValue =
  DestinationConfigFormValues[keyof DestinationConfigFormFieldTypeMap];

export type ConfigFormValues = DestinationConfigFormFieldTypeMap & {
  isValid: boolean;
};

/**
 * TEMPORARY SOLUTION FOR MLB DESTINATION CONFIG> WILL BE DELETING WHEN IMPLEMENTING CUSTOM FIELD CONFIGURATION
 **/
export type MlbConfigFormData = ExportConfigFormData & {
  is_mlb: boolean;
  privacySettings: MLBPrivacySettings;
  fieldMappings: ZetaFieldMappings;
};

export interface MLBPrivacySettings {
  losp: string;
  end_user_opt_in: boolean;
  third_party_name: string;
  club_sponsor_advertiser_io: string;
  opt_in_language_approved_vetted: boolean;
  club_sponsor_advertiser_data_share: string;
  info_collected_via_mlb_privacy_policy: boolean;
}

export interface ZetaFieldMappings {
  variation: string;
  email_type: string;
  campaign_id: string;
  email_list_id: string;
  export_frequency: string;
}

export interface CsvMappings {
  campaign_type: string;
}

export type AudienceSchedule = {
  startType?: AudienceScheduleStartTypeEnum;
  startDate?: ISOString;
  startTimezone?: {
    name: string;
    offset: string;
  };
  endType?: AudienceScheduleEndTypeEnum;
  endDate?: ISOString;
  endTimezone?: {
    name: string;
    offset: string;
  };
  frequency?: AudienceFrequencyEnum;
  weekdays?: Weekday[];
};

export type TableNameValidation = {
  hasInvalidCharacters: Nullable<boolean>;
  isDuplicate: Nullable<boolean>;
};
